import { action, computed, makeObservable, observable, reaction, toJS, runInAction } from 'mobx';
import {
	cloneDeep as _cloneDeep,
	isEmpty as _isEmpty,
	isEqual as _isEqual,
	keyBy as _keyBy,
	omit as _omit
} from 'lodash';
import { EtaCalculator, EtaResponseRouteItem, RunChangedParams } from '@bringg-frontend/eta-calculator';
import {
	CustomerAvailabilityHour,
	CustomAttributeRes,
	DeliveryBlock,
	Driver,
	Location,
	OPTIMIZATION_TYPE,
	ParkingSpot,
	Task,
	Team,
	Vehicle,
	VehicleType
} from '@bringg/types';
import { Notification } from '@bringg/react-components';
import moment, { Moment } from 'moment';
import type { Directions } from '@bringg/types/types/directions';
import { TaskVehiclePair } from '@bringg/dashboard-sdk/dist/Run/Service/runs.service';

import { createDriverByRunIdMap, createTasksByRunIdMap } from '../components/routes-planner/utils';
import { getFirstRunsIds, getInitialRoutesColors, getResequenceRoutesData } from './helpers/helpers';
import { updateRunPriorities } from '../stores/helpers/update-tasks';
import { getTasksByIdsInPresentedDate, orderRoutesByDate, orderTasksByDate } from './helpers/tasks-by-dates';
import { DEFAULT_TRANSLATIONS, RoutesPlannerTranslations } from '../components';
import MapStore from './map/map-store';
import TableStore from './table/table-store';
import GanttStore from './gantt/gantt-store';
import type { RoutesPlannerBreak, RoutesPlannerConfiguration } from '../components/routes-planner/routes-planner';
import type { RoutesPlannerProps } from '../RoutesPlannerApp';
import type { DataChangesNotification } from '../components/types';
import ColorGenerator from '../utils/color-generator';
import { getStartOfDay } from '../utils/date-utils';
import { errorNotifierWrapper } from '../services/error-notifier';
import type { RoutesPlannerRun, RoutesPlannerTask } from '../types';
import type { ConsecutiveRoutes } from '../types/routes-planner-run';
import ConsecutiveRoutesStore from './consecutive-routes/consecutive-routes-store';
import { etaResponseProcessor } from '../services/eta/eta-response-processor';
import { etaRoutesProvider } from '../services/eta/eta-routes-provider';
import { etaRoutesConverter } from '../services/eta/eta-routes-converter';
import { optimizationSavingData } from '../services/optimization-saving-data/optimization-saving-data';
import { OpenUserAssignerProps, UpdateVehicleOnRunProps } from '../types/routes-planner-app';
import OptimizationKpiStore, { OptimizationKpi } from './kpi/optimization-kpi-store';

const DEFAULT_DIRECTIONS: Directions[] = [];
const DEFAULT_BREAKS: { [runId: number]: RoutesPlannerBreak[] } = {};

export const NO_ROUTE_ID = -1;
export const NO_ROUTE_COLOR = '#8c9cb2';
export const INITIAL_PRESELECT_RUN_COUNT = 3;
export const INITIAL_PRESELECT_RUN_COUNT_GROUP_BY_DRIVER = 2;
export const UNASSIGN_ROUTE = 'unassign_route';

const TASK_COMPARISON_IGNORED_FIELDS = ['isDirty', 'way_points[0].has_to_leave_by'];

interface PreviousUserChange {
	tasksById: Map<number, RoutesPlannerTask>;
	allTasksById: Map<number, RoutesPlannerTask>;
	tasksByDate: Map<string, Map<number, RoutesPlannerTask>>;
	runToBreak: { [runId: number]: RoutesPlannerBreak[] };
	routeInfoByIndex: { [runId: number]: EtaResponseRouteItem };
	directions: Record<string, Directions>;
	lastMovedItem?: number[];
	consecutiveRoutes: ConsecutiveRoutes;
	optimizationKpiData: OptimizationKpi;
}

export default class RoutesPlannerStore {
	tableStore: TableStore;
	mapStore: MapStore;
	consecutiveRoutesStore: ConsecutiveRoutesStore;
	ganttStore: GanttStore;
	optimizationKpiStore: OptimizationKpiStore;
	etaCalculator: EtaCalculator;
	tasksById: Map<number, RoutesPlannerTask> = null;
	allTasksById: Map<number, RoutesPlannerTask> = null;
	allUnmodifiedTasksById: Map<number, RoutesPlannerTask> = null;
	previousUserChange: PreviousUserChange = null;
	routeInfoByIndex: { [runId: number]: EtaResponseRouteItem } = {};
	routes: RoutesPlannerRun[];
	runToBreaks: { [runId: number]: RoutesPlannerBreak[] } = null;
	driversById: Record<string, Driver> = null;
	routesById: Record<string, RoutesPlannerRun> = null;
	team?: Partial<Team>;
	configuration?: RoutesPlannerConfiguration;
	home: Location;
	translations: RoutesPlannerTranslations;
	onDataChanged: (changes: DataChangesNotification) => void;
	runsToDisplay: number[] = [];
	modifiedRuns: Set<number> = null;
	modifiedTasks: Partial<Bringg.Task>[][] = [];
	isLoading: boolean = null;
	isInitModal: boolean = null;
	optimizationRequestId: string = null;
	fromDispatch: boolean = null;
	inProgressEtaCalculations: Map<number, string> = null;
	currentUser: Partial<Bringg.User>;
	vehicleTypeByVehicleIds: { [vehicleId: number]: VehicleType };
	openOrderPage: (taskId: number) => void;
	colorsMap: Map<number, string> = null;
	selectedTaskId: number = null;
	onContactSupport: () => void;
	vehicles: Vehicle[] = null;
	openUserAssignerModal: (props: OpenUserAssignerProps) => void;
	openVehicleAssignerModal?: (
		runId: number,
		onClose: ({ runId, vehicleId, tasksIds }: UpdateVehicleOnRunProps) => void
	) => void;
	optimizationType: OPTIMIZATION_TYPE;
	shouldCalculateEtaGroupedByDriver: boolean;
	customerIdByTaskInventory: { [taskInventoryId: string]: number } = null;
	tasksByDate: Map<string, Map<number, RoutesPlannerTask>> = null;
	routesByDate: Record<string, RoutesPlannerRun[]>;
	sortedTasksDates: Moment[];
	presentedDate: Moment = null;
	deliveryBlocksByDriver: { [driverId: number]: DeliveryBlock[] };
	deliveryBlocksByVehicle: { [vehicleId: number]: DeliveryBlock[] };
	consecutiveRouteFF: boolean;
	multiSelectedEnabled: boolean;
	parkingSpots: ParkingSpot[];
	isReadOnly: boolean;
	customerAvailabilityHours: { [customerId: string]: CustomerAvailabilityHour[] };
	isOptimizationKpiEnabled: boolean;
	customAttributes: CustomAttributeRes[];
	originalRunToTasks: { [runId: number]: RoutesPlannerTask[] } = {};

	constructor(props: Partial<RoutesPlannerProps> & { translations: RoutesPlannerTranslations }) {
		const {
			tasks,
			routes,
			drivers,
			deliveryBlocks,
			translations = DEFAULT_TRANSLATIONS,
			configuration,
			onItemMoveRecalculateEtaFetch,
			team,
			currentUser,
			optimizationType,
			onDataChanged,
			optimizationRequestId,
			fromDispatch,
			vehicleTypeByVehicleIds,
			openOrderPage,
			onContactSupport,
			vehicles,
			openUserAssignerModal,
			openVehicleAssignerModal,
			customerIdByTaskInventory,
			consecutiveRoutes,
			mapIcons,
			parkingSpots,
			isReadOnly,
			customerAvailabilityHours,
			isOptimizationKpiEnabled,
			deps,
			customAttributes
		} = props;

		//reset ColorGenerator index
		ColorGenerator.resetIndex();

		// props for mapStore
		const { home, directions = DEFAULT_DIRECTIONS } = props;

		//props for ganttStore
		const { breaks = DEFAULT_BREAKS } = props;

		makeObservable(this, {
			tasksById: observable,
			allTasksById: observable,
			previousUserChange: observable,
			runToTasks: computed,
			runToTasksInPresentedDate: computed,
			runToBreaks: observable,
			driversById: observable,
			routesById: observable,
			runsToDisplay: observable,
			modifiedRuns: observable,
			modifiedTasks: observable,
			isLoading: observable,
			isInitModal: observable,
			inProgressEtaCalculations: observable,
			setRunsToDisplay: action,
			addModifiedRuns: action,
			addModifiedTasks: action,
			setIsLoading: action,
			setIsInitModal: action,
			updateRunToBreak: action,
			unassignTasksOnSameRoute: action,
			updateTasks: action,
			updateTask: action,
			executeEtaCalculationFlow: action,
			optimizationRequestId: observable,
			fromDispatch: observable,
			driversCount: computed,
			routesCount: computed,
			undoLastChange: action,
			isThereUserChange: computed,
			setPreviousState: action,
			inProgressEtaCalculationsList: computed,
			setInProgressEtaCalculations: action,
			isEtaHasActiveCalculation: action,
			allConfiguration: computed,
			displayedRoutesList: computed,
			selectedTaskId: observable,
			setSelectedTaskId: action,
			setRouteColor: action,
			colorsMap: observable,
			routesColors: computed,
			vehicles: observable,
			customerIdByTaskInventory: observable,
			presentedDate: observable,
			setPresentedDate: action,
			tasksByDate: observable,
			resetPreviousUserChange: action,
			updateTasksById: action,
			setTasksState: action,
			setUnmodifiedTasksState: action,
			updateVehicleOnRun: action
		});

		this.deliveryBlocksByDriver = {};

		if (optimizationType === OPTIMIZATION_TYPE.COMBINED) {
			this.deliveryBlocksByVehicle = {};

			for (const deliveryBlock of deliveryBlocks) {
				const driverIds = deliveryBlock.delivery_block_resources.map(x => x.user_id).filter(Boolean);
				const vehicleIds = deliveryBlock.delivery_block_resources.map(x => x.vehicle_id).filter(Boolean);

				this.addDeliveryBlockByDriverOrVehicle(driverIds, this.deliveryBlocksByDriver, deliveryBlock);
				this.addDeliveryBlockByDriverOrVehicle(vehicleIds, this.deliveryBlocksByVehicle, deliveryBlock);
			}
		} else {
			for (const deliveryBlock of deliveryBlocks) {
				this.addDeliveryBlockByDriverOrVehicle(
					deliveryBlock.user_ids,
					this.deliveryBlocksByDriver,
					deliveryBlock
				);
			}
		}

		this.runToBreaks = breaks;
		this.driversById = drivers;
		this.routesById = routes;
		this.configuration = configuration;
		this.translations = translations;
		this.parkingSpots = parkingSpots;
		this.team = {
			...team,
			time_zone: deps.getTimezoneByTeamId(team.id)
		};
		this.optimizationRequestId = optimizationRequestId;
		this.fromDispatch = fromDispatch;
		this.inProgressEtaCalculations = new Map();
		this.currentUser = currentUser;
		this.vehicleTypeByVehicleIds = vehicleTypeByVehicleIds || {};
		this.openOrderPage = openOrderPage;
		this.onContactSupport = onContactSupport;
		this.vehicles = vehicles;
		this.openUserAssignerModal = openUserAssignerModal;
		this.openVehicleAssignerModal = openVehicleAssignerModal;
		this.optimizationType = optimizationType;
		this.customerIdByTaskInventory = customerIdByTaskInventory;
		this.setTasksState(tasks);
		this.setUnmodifiedTasksState(tasks);
		this.routesByDate = orderRoutesByDate(routes, this.team.time_zone);
		this.modifiedRuns = new Set();
		this.customAttributes = customAttributes || [];

		this.colorsMap = getInitialRoutesColors(this.routesById);

		this.etaCalculator = onItemMoveRecalculateEtaFetch
			? new EtaCalculator(onItemMoveRecalculateEtaFetch, null, currentUser)
			: null;
		this.isLoading = false;
		this.consecutiveRoutesStore = new ConsecutiveRoutesStore(this, consecutiveRoutes);
		this.tableStore = new TableStore(this, { customerAvailabilityHours });
		this.ganttStore = new GanttStore(this);
		this.mapStore = new MapStore(this, {
			directions,
			home,
			mapIcons,
			deliveryBlocks
		});

		this.isOptimizationKpiEnabled = isOptimizationKpiEnabled;
		if (isOptimizationKpiEnabled) {
			this.optimizationKpiStore = new OptimizationKpiStore(
				this,
				configuration.kpi,
				deps.fetchOptimizationKpiData
			);
		}

		this.shouldCalculateEtaGroupedByDriver =
			currentUser?.feature_flags && currentUser.feature_flags['calculate_eta_group_by_driver'];

		this.consecutiveRouteFF = currentUser?.feature_flags && currentUser.feature_flags['consecutive_routes'];
		this.multiSelectedEnabled =
			currentUser?.feature_flags && currentUser.feature_flags['routes_planner_multiselect'];
		this.isReadOnly = isReadOnly;

		this.originalRunToTasks = _cloneDeep(this.runToTasks);

		this.initRunsToDisplay();

		reaction(
			() => this.runToTasks,
			runsToTasks => {
				const consecutiveRoutesEnabled = this.consecutiveRouteFF && this.shouldCalculateEtaGroupedByDriver;

				const { runToTasks, runToBreaks, routeInfoByIndex, runToDriver } = optimizationSavingData.prepareData(
					runsToTasks,
					this.runToBreaks,
					this.routeInfoByIndex,
					this.runToDriver,
					this.consecutiveRoutesStore,
					consecutiveRoutesEnabled
				);

				const getUniqueModifiedTasks = () =>
					this.modifiedTasks.flatMap(i => i).filter((v, i, a) => a.findIndex(v2 => v2.id === v.id) === i);

				onDataChanged({
					runToTasks,
					runToBreaks,
					runToDriver,
					routeInfoByIndex,
					modifiedTasks: getUniqueModifiedTasks()
				});
			}
		);
	}

	private addDeliveryBlockByDriverOrVehicle(resourceIds, container, deliveryBlock) {
		for (const resourceId of resourceIds) {
			if (!container[resourceId]) {
				container[resourceId] = [];
			}

			container[resourceId].push(deliveryBlock);
		}
	}

	initRunsToDisplay = () => {
		const runsToPreselect = getFirstRunsIds(
			this.routesByDate[this.presentedDate?.toISOString()] || [],
			this.optimizationType,
			INITIAL_PRESELECT_RUN_COUNT
		);
		this.setRunsToDisplay(runsToPreselect, { shouldReplaceList: true });
	};

	setUnmodifiedTasksState = (tasks: RoutesPlannerTask[]) => {
		this.allUnmodifiedTasksById = new Map(_cloneDeep(tasks).map(task => [task.id, task]));
	};

	setTasksState = (tasks: RoutesPlannerTask[]) => {
		this.tasksByDate = orderTasksByDate(tasks, this.routesById, this.routeInfoByIndex, this.team.time_zone);
		this.sortedTasksDates = Array.from(this.tasksByDate.keys())
			.filter(date => date !== UNASSIGN_ROUTE)
			.sort((a, b) => {
				return new Date(a).valueOf() - new Date(b).valueOf();
			})
			.map(date => moment(date));
		this.allTasksById = new Map(tasks.map(task => [task.id, task]));
		this.presentedDate = this.sortedTasksDates[0];
		this.tasksById = getTasksByIdsInPresentedDate(this.tasksByDate, this.presentedDate, this.team.time_zone);
	};

	get getCurrentUser() {
		return this.currentUser;
	}

	get allConfiguration() {
		return this.configuration;
	}

	get vehiclesById() {
		return _keyBy(this.vehicles, 'id');
	}

	get runToTasksInPresentedDate(): { [runId: number]: RoutesPlannerTask[] } {
		return createTasksByRunIdMap(Array.from(this.tasksById.values()));
	}

	get runToTasks(): { [runId: number]: RoutesPlannerTask[] } {
		return createTasksByRunIdMap(Array.from(this.allTasksById.values()));
	}

	get activeDraggedTask() {
		const taskId = this.tableStore.taskIdOfActiveDraggableItem;
		if (taskId) {
			return toJS(this.tasksById.get(taskId));
		}
		return null;
	}

	get driversCount() {
		const routesByDate: RoutesPlannerRun[] = this.routesByDate[this.presentedDate?.toISOString()];
		return routesByDate
			? new Set(
					Object.values(routesByDate)
						.map(route => route.user_id)
						.filter(Boolean)
			  ).size
			: 0;
	}

	get routesCount() {
		const routesByDate = this.routesByDate[this.presentedDate?.toISOString()];
		return routesByDate ? Object.keys(routesByDate).filter(id => `${id}` !== `null`).length : 0;
	}

	get vehiclesCount() {
		const routesByDate = this.routesByDate[this.presentedDate?.toISOString()];
		return routesByDate
			? new Set(
					Object.values(routesByDate)
						.map(route => route.vehicle_id)
						.filter(vehicleId => vehicleId && !this.vehiclesById[vehicleId].is_trailer)
			  ).size
			: 0;
	}

	get shouldShowVehicles() {
		return this.optimizationType === OPTIMIZATION_TYPE.COMBINED && this.configuration.enableVehicles;
	}

	get shouldShowTrailer() {
		return this.shouldShowVehicles && !!Object.values(this.vehiclesById).find(vehicle => vehicle.is_trailer);
	}

	setPreviousState = (changedItemsIds?: number[]) => {
		const currentTaskByDate = new Map();
		this.tasksByDate.forEach((values, keys) => currentTaskByDate.set(keys, new Map(values)));

		this.previousUserChange = {
			routeInfoByIndex: JSON.parse(JSON.stringify({ ...this.routeInfoByIndex })),
			allTasksById: new Map(this.allTasksById),
			tasksByDate: currentTaskByDate,
			tasksById: new Map(this.tasksById),
			runToBreak: { ...this.runToBreaks },
			directions: { ...this.mapStore.runToDirections },
			consecutiveRoutes: {
				routesById: new Map(this.consecutiveRoutesStore.routesById),
				tasksById: new Map(this.consecutiveRoutesStore.tasksById)
			},
			lastMovedItem: changedItemsIds,
			optimizationKpiData: this.isOptimizationKpiEnabled
				? { ...this.optimizationKpiStore.optimizationKpiData }
				: ({} as OptimizationKpi)
		};
	};

	setPresentedDate = (date: Moment) => {
		this.presentedDate = date;
	};

	updateTasksById = () => {
		this.tasksById = getTasksByIdsInPresentedDate(this.tasksByDate, this.presentedDate, this.team.time_zone);
	};

	executeEtaCalculationFlow = async (
		routesDataToCalculate: RunChangedParams[],
		tasksToUpdateByRun: { [runId: number]: RoutesPlannerTask[] },
		shouldSavePreviousChange: boolean,
		changedItemsIds?: number[],
		shouldIgnoreKpiFetch?: boolean
	) => {
		if (this.isReadOnly) {
			return;
		}
		let etaCalculationsResult = [];

		if (shouldSavePreviousChange) {
			this.setPreviousState(changedItemsIds);
			this.addModifiedRuns(routesDataToCalculate.map(route => route.runId));
		}
		this.ganttStore.setLastUpdatedRunIds(routesDataToCalculate.map(route => route.runId));

		if (this.shouldCalculateEtaGroupedByDriver) {
			const routesGroupedByResources = etaRoutesProvider.getGroupedByResource(routesDataToCalculate, this);

			if (routesGroupedByResources.length > 0) {
				etaCalculationsResult = await this.etaCalculator.runEtaCalculations(
					routesGroupedByResources,
					this.setInProgressEtaCalculations,
					this.optimizationType,
					this.team.id
				);
			}

			// eslint-disable-next-line no-param-reassign
			tasksToUpdateByRun = routesGroupedByResources
				.map(group => group.routes)
				.flat()
				.reduce((dictionary, route) => {
					dictionary[route.runId] = route.runOrderedTasks;
					return dictionary;
				}, {});
		} else {
			etaCalculationsResult = await this.etaCalculator.runEtaCalculation(
				routesDataToCalculate,
				this.setInProgressEtaCalculations,
				this.optimizationType,
				this.team.id
			);
		}

		const {
			runToTaskUpdate,
			runToBreaksUpdate,
			runToDirectionUpdate,
			routeInfoByIndex,
			updatedConsecutiveRoutes,
			updatedConsecutiveTasks,
			updatedConsecutiveBreaks
		} = etaResponseProcessor.process(
			etaCalculationsResult,
			tasksToUpdateByRun,
			this.routesById,
			this.routeInfoByIndex,
			this.consecutiveRoutesStore.routesById,
			this.consecutiveRoutesStore.tasksById
		);

		runInAction(() => {
			this.tableStore.setSkipTablePageReset(true);
			this.updateRouteInfo(routeInfoByIndex);
			this.updateTasks(runToTaskUpdate);
			this.updateRunToBreak(runToBreaksUpdate);
			this.consecutiveRoutesStore.batchUpdateRoutes(updatedConsecutiveRoutes);
			this.consecutiveRoutesStore.batchUpdateTasks(updatedConsecutiveTasks);
			this.consecutiveRoutesStore.batchUpdateBreaks(updatedConsecutiveBreaks);
			this.mapStore.setDirections(runToDirectionUpdate);
		});

		if (this.isOptimizationKpiEnabled && !shouldIgnoreKpiFetch) {
			await this.optimizationKpiStore.fetchUpdatedKpiData(false);
		}
	};

	setRunToBreaks = runToBreaksUpdate => {
		Object.assign(this.runToBreaks, runToBreaksUpdate);
	};

	setRouteInfoByIndex = routeInfoByIndexUpdate => {
		Object.assign(this.routeInfoByIndex, routeInfoByIndexUpdate);
	};

	get inProgressEtaCalculationsList() {
		return toJS(this.inProgressEtaCalculations);
	}

	isEtaHasActiveCalculation(runId: number) {
		return this.inProgressEtaCalculations.has(runId);
	}

	setInProgressEtaCalculations = (runId: number, requestUuid?: string) => {
		if (this.isEtaHasActiveCalculation(runId)) {
			this.inProgressEtaCalculations.delete(runId);
		} else {
			this.inProgressEtaCalculations.set(runId, requestUuid);
		}
	};

	get isThereUserChange() {
		return !!this.previousUserChange;
	}

	resetPreviousUserChange = () => {
		this.previousUserChange = undefined;
	};

	undoLastChange = () => {
		if (!this.isReadOnly && this.isThereUserChange) {
			this.tasksById = this.previousUserChange.tasksById;
			this.allTasksById = this.previousUserChange.allTasksById;
			this.tasksByDate = this.previousUserChange.tasksByDate;
			this.setRunToBreaks(this.previousUserChange.runToBreak);
			if (Object.keys(this.previousUserChange.routeInfoByIndex).length) {
				this.setRouteInfoByIndex(this.previousUserChange.routeInfoByIndex);
			} else {
				this.routeInfoByIndex = {};
			}
			this.mapStore.setDirections(this.previousUserChange.directions);
			this.ganttStore.setSelectedItems(this.previousUserChange.lastMovedItem);
			this.consecutiveRoutesStore.setRoutes({
				tasksById: new Map(this.previousUserChange.consecutiveRoutes.tasksById),
				routesById: new Map(this.previousUserChange.consecutiveRoutes.routesById)
			});
			if (this.isOptimizationKpiEnabled) {
				this.optimizationKpiStore.setOptimizationKpiData(this.previousUserChange.optimizationKpiData);
			}
			this.resetPreviousUserChange();
		}

		if (this.modifiedTasks.length) {
			this.modifiedTasks.pop();
		}
	};

	setRunsToDisplay = async (
		runIds: number[],
		runToDisplayOptions: { show?: boolean; shouldReplaceList?: boolean }
	) => {
		const { show, shouldReplaceList } = runToDisplayOptions;
		if (shouldReplaceList) {
			this.runsToDisplay = runIds;
			await this.loadMissingDirections(runIds);
		} else {
			if (show) {
				this.runsToDisplay = this.runsToDisplay.concat(runIds);
				await this.loadMissingDirections(runIds);
			} else {
				this.runsToDisplay = this.runsToDisplay.filter(id => runIds.indexOf(id) === -1);
			}
		}
		if (this.isOptimizationKpiEnabled) {
			await this.optimizationKpiStore.fetchUpdatedKpiData(true);
		}
	};

	addModifiedRuns = (runIds: number[]) => {
		runIds.forEach(runId => this.modifiedRuns.add(runId));
	};

	addModifiedTasks = (
		taskRouteId: number,
		tasksModifiedByUser: Partial<Bringg.Task>[],
		tasksByRunWithUpdatedState
	) => {
		const tasks = this.getModifiedTasks(taskRouteId, tasksModifiedByUser, tasksByRunWithUpdatedState);

		if (!tasks.length) {
			return;
		}

		this.modifiedTasks.push(tasks);
	};

	setIsLoading = (isLoading: boolean) => {
		this.isLoading = isLoading;
	};

	setIsInitModal = (isInit: boolean) => {
		this.isInitModal = isInit;
	};

	updateRunToBreak = (runToBreakUpdate: { [runId: number]: RoutesPlannerBreak[] }) => {
		Object.assign(this.runToBreaks, runToBreakUpdate);
	};

	updateRouteInfo = (routeInfoByIndex: { [runId: number]: EtaResponseRouteItem }) => {
		Object.keys(routeInfoByIndex).forEach(runId => {
			this.routeInfoByIndex[runId] = routeInfoByIndex[runId];
		});
	};

	updateTasks = (tasksToUpdateByRun: { [runId: number]: RoutesPlannerTask[] }) => {
		Object.keys(tasksToUpdateByRun).forEach(runId => {
			const tasks = tasksToUpdateByRun[runId];

			tasks.forEach(task => {
				this.updateTask(task);
			});
		});
	};

	updateTask = (task: RoutesPlannerTask) => {
		const unmodifiedTask = _omit(this.allUnmodifiedTasksById.get(task.id), TASK_COMPARISON_IGNORED_FIELDS);
		task.isDirty = !_isEqual(unmodifiedTask, _omit(task, TASK_COMPARISON_IGNORED_FIELDS));
		this.tasksById.set(task.id, task);
		this.allTasksById.set(task.id, task);
		const presentedDate = getStartOfDay(this.presentedDate, this.team.time_zone);
		this.tasksByDate.get(presentedDate).set(task.id, task);
	};

	updateTasksByDateOnUnassignTask = (tasksToUnassigned: Task[], presentedDate: string) => {
		if (!this.tasksByDate.has(UNASSIGN_ROUTE)) {
			this.tasksByDate.set(UNASSIGN_ROUTE, new Map());
		}

		tasksToUnassigned.forEach(task => {
			this.tasksByDate.get(UNASSIGN_ROUTE).set(task.id, task);
			this.tasksByDate.get(presentedDate).delete(task.id);
		});
	};

	unassignTasksOnSameRoute = async (taskIds: number[]) => {
		const currentTasks = taskIds.map(taskId => this.tasksById.get(taskId));
		const currentRunId = currentTasks[0].run_id;
		const linkedTaskIds = currentTasks.filter(task => task.linked_task_id).map(task => task.linked_task_id);

		const tasksToRecalculate = this.runToTasksInPresentedDate[currentRunId].filter(
			task => !(taskIds.includes(task.id) || linkedTaskIds.includes(task.id))
		);
		let shouldUpdateTaskLocally = true;

		if (tasksToRecalculate.length || this.shouldCalculateEtaGroupedByDriver) {
			const routesDataToCalculate = etaRoutesConverter.routesToRunsChangedParams(
				[currentRunId],
				this.runToTasksInPresentedDate,
				this.runToBreaks,
				this.routesById
			);
			routesDataToCalculate[0].runOrderedTasks = tasksToRecalculate;
			routesDataToCalculate[0].driverBreaks = this.runToBreaks[currentRunId];

			try {
				await errorNotifierWrapper({
					callback: async () =>
						this.executeEtaCalculationFlow(
							routesDataToCalculate,
							{
								[currentRunId]: this.runToTasksInPresentedDate[currentRunId]
							},
							true
						),
					errorMessagesMap: this.translations.errorMessages
				});
				const hasLinkedTaskIdThatNotSelected = linkedTaskIds.filter(
					linkedTaskId => !taskIds.includes(linkedTaskId)
				).length;
				if (hasLinkedTaskIdThatNotSelected) {
					Notification.info(this.translations.notificationsTranslations.unassignedLinkedTasks);
				}
			} catch (e) {
				console.error(e);
				shouldUpdateTaskLocally = false;
				return false;
			}
		} else {
			this.clearRunDirectionsAndBreaks(currentRunId);
		}

		if (shouldUpdateTaskLocally) {
			this.tableStore.setSkipTablePageReset(true);
			const updatedTasks = currentTasks.map(task => ({
				...task,
				user: null,
				user_id: null,
				run_id: NO_ROUTE_ID,
				priority: task.id
			}));

			const presentedDate = this.presentedDate.toISOString();

			if (linkedTaskIds.length) {
				const linkedTasks = linkedTaskIds.map(linkedTaskId => this.tasksById.get(linkedTaskId));

				const updatedLinkedTasks = linkedTasks.map(linkedTask => ({
					...linkedTask,
					user: null,
					user_id: null,
					run_id: NO_ROUTE_ID,
					priority: linkedTask.id
				}));

				const updatedTasksAndLinkedTasks = updatedTasks.concat(updatedLinkedTasks);
				const tasksToUpdate: { [runId: number]: Task[] } = {
					[currentRunId]: updatedTasksAndLinkedTasks
				};

				this.updateTasksByDateOnUnassignTask(updatedTasksAndLinkedTasks, presentedDate);
				this.updateTasks(tasksToUpdate);
			} else {
				this.updateTasksByDateOnUnassignTask(updatedTasks, presentedDate);
				updatedTasks.forEach(task => this.updateTask(task));
			}

			if (!this.runToTasks[currentRunId]?.length) {
				this.clearRunDirectionsAndBreaks(currentRunId);
			}
		}
		return true;
	};

	createUpdatedTask = (taskId: number, runId: number, eta: Date) => {
		const currentTask = this.tasksById.get(taskId);
		const wp = {
			...currentTask.way_points[0],
			eta: eta.toISOString(),
			scheduled_at: eta.toISOString()
		};
		return {
			...currentTask,
			run_id: runId,
			user_id: this.runToDriver[runId]?.id,
			vehicle_id: this.runToTasksInPresentedDate[runId][0].vehicle_id,
			way_points: [wp, ...currentTask.way_points.slice(1)]
		};
	};

	assignedTasksToRun = async (updateTasksData: { taskId: number; runId: number; eta: Date }[]) => {
		const routesDataToCalculate: { [routeId: number]: RunChangedParams } = {};
		const tasksToRecalculate = {};
		updateTasksData.forEach(task => {
			const { taskId, runId, eta } = task;
			const updatedTask = this.createUpdatedTask(taskId, runId, eta);
			tasksToRecalculate[runId] = [
				...(tasksToRecalculate[runId] || this.runToTasksInPresentedDate[runId] || []),
				updatedTask
			];
			const [runOrderedTasks, driverBreaks] = updateRunPriorities(
				tasksToRecalculate[runId],
				this.runToBreaks[runId]
			);

			routesDataToCalculate[runId] = etaRoutesConverter.routeToRunChangedParams(
				this.routeInfoByIndex[runId] || this.routesById[runId],
				runOrderedTasks,
				driverBreaks
			);
		});
		try {
			await errorNotifierWrapper({
				callback: async () =>
					this.executeEtaCalculationFlow(Object.values(routesDataToCalculate), tasksToRecalculate, true),
				errorMessagesMap: this.translations.errorMessages
			});
		} catch (e) {
			console.error('assignedTasksToRun: ', e);
			return false;
		}

		this.tableStore.setSkipTablePageReset(true);
	};

	showWarningIfSomeTaskIsOutsideTimeWindow = (movedTask: Task[]) => {
		const tasksIdsOutsideTimeWindow = movedTask
			.filter(
				task =>
					moment(task.way_points[0].etl).isAfter(task.way_points[0].no_later_than) ||
					moment(task.way_points[0].eta).isBefore(task.way_points[0].no_earlier_than)
			)
			.map(task => task.id);

		if (tasksIdsOutsideTimeWindow.length) {
			let message = '';
			if (tasksIdsOutsideTimeWindow.length > 1) {
				message = `${tasksIdsOutsideTimeWindow.length} ${this.translations.notificationsTranslations.orders} ${this.translations.notificationsTranslations.outsidePromisedWindow}`;
			} else {
				message = `${this.translations.notificationsTranslations.order} ${tasksIdsOutsideTimeWindow.join(
					', '
				)} ${this.translations.notificationsTranslations.outsidePromisedWindow}`;
			}

			Notification.warn(message);
		}
	};

	resequenceTasks = async (runId: number, tasksIds: number[], priority: number) => {
		const tasksToMove = tasksIds
			.map(taskId => toJS(this.tasksById.get(taskId)))
			.sort((taskA, taskB) => taskA.priority - taskB.priority);

		if (this.inProgressEtaCalculations.has(runId) || this.inProgressEtaCalculations.has(tasksToMove[0].run_id)) {
			return;
		}
		const linkedTasksThatNotSelected = tasksToMove
			.filter(task => task.linked_task_id && !tasksIds.includes(task.linked_task_id))
			.map(task => toJS(this.tasksById.get(task.linked_task_id)));

		const prevChangedTasksInfo: Partial<Bringg.Task>[] = [...tasksToMove, ...linkedTasksThatNotSelected].map(
			task => ({
				id: task.id,
				run_id: task.run_id,
				priority: task.priority
			})
		);

		if (this.etaCalculator) {
			const changedRoutesData = getResequenceRoutesData({
				tasksToMove,
				linkedTasksThatNotSelected,
				currentRunId: runId,
				currentPriority: priority,
				runToTasksInPresentedDate: this.runToTasksInPresentedDate,
				runToBreaks: this.runToBreaks,
				runToDriver: this.runToDriver,
				notificationsTranslations: this.translations.notificationsTranslations,
				routesById: this.routesById,
				routeInfoByIndex: this.routeInfoByIndex
			}).filter(item => item.runId !== NO_ROUTE_ID);

			const tasksToUpdateByRun = changedRoutesData.reduce((dictionary, data) => {
				dictionary[data.runId] = data.runOrderedTasks;

				return dictionary;
			}, {});

			this.addModifiedTasks(runId, prevChangedTasksInfo, tasksToUpdateByRun);

			try {
				await errorNotifierWrapper({
					callback: async () => this.executeEtaCalculationFlow(changedRoutesData, tasksToUpdateByRun, true),
					errorMessagesMap: this.translations.errorMessages
				});
				const emptyRoute = changedRoutesData.find(item => item.runOrderedTasks.length === 0);

				if (emptyRoute) {
					this.clearRunDirectionsAndBreaks(emptyRoute.runId);
				}

				this.showWarningIfSomeTaskIsOutsideTimeWindow(tasksToMove);

				this.ganttStore.selectTaskOnGantt(tasksIds);
				return true;
			} catch (e) {
				console.error(e);

				return false;
			}
		}
	};

	clearRunDirectionsAndBreaks = (runId: number) => {
		this.mapStore.runToDirections[runId] = null;
		this.runToBreaks[runId] = [];
		if (this.isRunDisplayed(runId)) {
			this.setRunsToDisplay([runId], { show: false });
		}
	};

	loadMissingDirections = async (runIds: number[]): Promise<boolean> => {
		const runIdsWithNoDirections = this.mapStore.getRunIdsWithNoDirections(runIds);

		if (!_isEmpty(runIdsWithNoDirections) && this.etaCalculator) {
			const taskRunData = etaRoutesConverter.routesToRunsChangedParams(
				runIdsWithNoDirections,
				this.runToTasksInPresentedDate,
				this.runToBreaks,
				this.routesById
			);
			if (taskRunData.length) {
				try {
					await errorNotifierWrapper({
						callback: async () =>
							this.executeEtaCalculationFlow(
								taskRunData,
								this.runToTasksInPresentedDate,
								false,
								null,
								true
							),
						errorMessagesMap: this.translations.errorMessages
					});
					return true;
				} catch (e) {
					console.error('loadMissingDirections: ', e);
				}
			}
		}
		return false;
	};

	isRunDisplayed = runId => {
		return this.runsToDisplay.findIndex(item => runId === item) > -1;
	};

	isRunModified = runId => this.modifiedRuns.has(runId);

	refresh = async (tasksById: Record<string, RoutesPlannerTask>, runIdsToUpdate: number[]) => {
		this.tableStore.setSkipTablePageReset(true);
		const tasks = Object.values(tasksById);
		this.setTasksState(tasks);
		this.setUnmodifiedTasksState(tasks);

		if (runIdsToUpdate.length > 0) {
			const taskRunData = etaRoutesConverter.routesToRunsChangedParams(
				runIdsToUpdate,
				this.runToTasksInPresentedDate,
				this.runToBreaks,
				this.routesById
			);
			if (taskRunData.length) {
				try {
					await this.executeEtaCalculationFlow(taskRunData, this.runToTasksInPresentedDate, false);
				} catch (e) {
					console.error('Real time error: ', e);
				}
			}
		}
	};

	get displayedRoutesList() {
		return Object.values(this.routesById)
			.filter(route => this.isRunDisplayed(route.id))
			.map(({ id, title }) => {
				const color = this.colorsMap.get(id);
				const isLoading = this.inProgressEtaCalculationsList.has(id);

				return {
					id,
					title,
					color,
					length: this.runToTasksInPresentedDate[id]?.length || 0,
					isLoading
				};
			});
	}

	get runToDriver(): { [runId: number]: Driver } {
		return createDriverByRunIdMap(this.driversById, this.allTasksById);
	}

	get routesColors() {
		return toJS(this.colorsMap);
	}

	setRouteColor = (runId: number, color: string, recordChange = true) => {
		if (recordChange) this.ganttStore.lastUpdatedRunIds = [runId];
		this.colorsMap.set(runId, color);
	};

	setSelectedTaskId = (taskId: number) => {
		this.selectedTaskId = taskId;
	};

	updateDriverOnRun = async (runId: number, driver: Driver) => {
		this.runToDriver[runId] = driver;

		this.runToTasksInPresentedDate[runId].forEach(task => {
			task.user_id = driver.id;
		});

		this.routesById[runId].user_id = driver.id;
		const routeToUpdate = this.routesByDate[this.presentedDate.toISOString()].find(route => route.id === runId);
		routeToUpdate.user_id = driver.id;

		const taskRunData = etaRoutesConverter.routesToRunsChangedParams(
			[runId],
			this.runToTasksInPresentedDate,
			this.runToBreaks,
			this.routesById
		);

		await errorNotifierWrapper({
			callback: async () => this.executeEtaCalculationFlow(taskRunData, this.runToTasksInPresentedDate, false),
			errorMessagesMap: this.translations.errorMessages,
			errorParams: { source: 'updateDriverOnRun', runId, userId: driver.id },
			shouldRaiseError: false
		});
	};

	updateVehicleOnStore = (vehicleId: number, taskVehiclePair: TaskVehiclePair[], runId: number) => {
		const tasksByTrailerId = {};
		taskVehiclePair.forEach(task => {
			tasksByTrailerId[task.id] = task.vehicle_id;
		});

		this.routesById[runId].vehicle_id = vehicleId;

		this.runToTasksInPresentedDate[runId].forEach(task => {
			task.vehicle_id = tasksByTrailerId[task.id];
		});

		const routeToUpdate = this.routesByDate[this.presentedDate.toISOString()].find(route => route.id === runId);
		routeToUpdate.vehicle_id = vehicleId;
	};

	updateVehicleOnRun = async ({ runId, vehicleId, tasksIds }: UpdateVehicleOnRunProps) => {
		const previousVehicleId = this.routesById[runId].vehicle_id;

		try {
			this.updateVehicleOnStore(vehicleId, tasksIds, runId);
			const taskRunData = etaRoutesConverter.routesToRunsChangedParams(
				[runId],
				this.runToTasksInPresentedDate,
				this.runToBreaks,
				this.routesById
			);

			await errorNotifierWrapper({
				callback: async () =>
					this.executeEtaCalculationFlow(taskRunData, this.runToTasksInPresentedDate, false),
				errorMessagesMap: this.translations.errorMessages,
				errorParams: { source: 'updateVehicleOnRun', runId, vehicleId }
			});
		} catch (e) {
			this.updateVehicleOnStore(previousVehicleId, tasksIds, runId);
		}
	};

	getModifiedTasks = (
		taskRouteId: number,
		tasksModifiedByUser: Partial<Bringg.Task>[],
		tasksByRunWithUpdatedState
	): Partial<Bringg.Task>[] => {
		return tasksModifiedByUser.flatMap(modifiedTask => {
			const updatedStateTask = tasksByRunWithUpdatedState[taskRouteId]?.find(
				runTask => modifiedTask.id === runTask.id
			);

			const isTaskChanged =
				updatedStateTask &&
				(modifiedTask.priority !== updatedStateTask.priority ||
					modifiedTask.run_id !== updatedStateTask.run_id);

			const res: { id: number }[] = [];

			if (isTaskChanged) {
				res.push({ id: modifiedTask.id });
			}

			if (isTaskChanged && modifiedTask.linked_task_id) {
				res.push({ id: modifiedTask.linked_task_id });
			}

			return res;
		});
	};
}
